import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Navigate, useParams } from "react-router-dom";
import { ChevronLeft, ChevronRight, Download } from "lucide-react";
import useEmblaCarousel from "embla-carousel-react";
import { toast } from "sonner";
import { api, formatApiDetail } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { BLUPreview, InvoiceSection } from "../../components/BLUPreview";
import {
  blobLooksLikeApiError,
  downloadFilenameFromHeaders,
  readApiErrorDetail,
  resolveUploadedContentType,
  uploadedFileName,
} from "../../lib/portalFiles";
import { portalPath, slugifyClientName } from "../../lib/portalPaths";

/** Old `/files/packet/:fileId` bookmarks → clean `/files/scope`. */
export function ScopePacketLegacyRedirect() {
  const { clientSlug } = useParams();
  const { client } = useAuth() || {};
  const target = client
    ? portalPath(client, "files/scope")
    : clientSlug
      ? `/${slugifyClientName(clientSlug)}/files/scope`
      : "/";
  return <Navigate to={target} replace />;
}

/**
 * In-app HTML viewer for Library scope (+ invoice) packets — same document feel as
 * proposals / agreements, with Download PDF for the stored file.
 * Public URL: `/{clientSlug}/files/scope` (no portal login required).
 */
export default function ScopePacketPage() {
  const { clientSlug: slugParam } = useParams();
  const { client } = useAuth() || {};
  const slug = slugifyClientName(slugParam || clientSlugFallback(client) || "");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [packet, setPacket] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [page, setPage] = useState(0); // 0 = scope, 1 = invoice
  const slideRefs = useRef([]);
  const [trackHeight, setTrackHeight] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!slug) {
        setError("Scope not found");
        setLoading(false);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const { data } = await api.get(`/public/files/scope/${encodeURIComponent(slug)}`);
        if (cancelled) return;
        setPacket(data);
        const company = (data?.payload?.client_name || client?.name || "Partner").trim();
        document.title = `${company} x TAĪSTU`;
      } catch (e) {
        if (!cancelled) {
          setError(formatApiDetail(e.response?.data?.detail, "Could not open scope packet"));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      document.title = "TAĪSTU | Client Portal";
    };
  }, [slug, client?.name]);

  const payload = packet?.payload || null;
  const scopeOnly = Boolean(packet?.scope_only || payload?.scope_only);
  const showInvoice = Boolean(payload && !scopeOnly);

  const pages = useMemo(() => {
    if (!payload) return [];
    if (!showInvoice) return [{ kind: "scope", label: "Scope" }];
    return [
      { kind: "scope", label: "Scope" },
      { kind: "invoice", label: "Invoice" },
    ];
  }, [payload, showInvoice]);

  const pageCount = pages.length;
  const safePage = Math.min(page, Math.max(0, pageCount - 1));
  const activeLabel = pages[safePage]?.label || "Scope";

  useEffect(() => {
    if (!showInvoice && page !== 0) setPage(0);
  }, [showInvoice, page]);

  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: "start",
    containScroll: "trimSnaps",
    dragFree: false,
    loop: false,
    skipSnaps: false,
    watchDrag: pageCount > 1,
  });

  const measureActive = useCallback(() => {
    const slide = slideRefs.current[safePage];
    if (!slide) return;
    setTrackHeight(slide.offsetHeight);
  }, [safePage]);

  useEffect(() => {
    measureActive();
    const slide = slideRefs.current[safePage];
    if (!slide || typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(() => measureActive());
    ro.observe(slide);
    return () => ro.disconnect();
  }, [safePage, pages, measureActive, payload]);

  useEffect(() => {
    if (!emblaApi) return;
    if (emblaApi.selectedScrollSnap() !== safePage) {
      emblaApi.scrollTo(safePage, true);
    }
  }, [emblaApi, safePage]);

  useEffect(() => {
    if (!emblaApi) return undefined;
    const onSelect = () => {
      const next = emblaApi.selectedScrollSnap();
      if (next !== page) setPage(next);
    };
    const onSettle = () => measureActive();
    emblaApi.on("select", onSelect);
    emblaApi.on("reInit", onSelect);
    emblaApi.on("settle", onSettle);
    return () => {
      emblaApi.off("select", onSelect);
      emblaApi.off("reInit", onSelect);
      emblaApi.off("settle", onSettle);
    };
  }, [emblaApi, page, measureActive]);

  useEffect(() => {
    if (!emblaApi) return;
    emblaApi.reInit({ watchDrag: pageCount > 1 });
  }, [emblaApi, pageCount, pages.length]);

  const goToPage = useCallback(
    (next) => {
      const clamped = Math.max(0, Math.min(pageCount - 1, next));
      setPage(clamped);
      emblaApi?.scrollTo(clamped);
    },
    [emblaApi, pageCount],
  );

  const subtitle = useMemo(() => {
    return (
      packet?.timeline_label ||
      payload?.timeline ||
      payload?.date_range ||
      packet?.note ||
      ""
    );
  }, [packet?.timeline_label, packet?.note, payload?.timeline, payload?.date_range]);

  const handleDownload = useCallback(async () => {
    if (!slug || downloading) return;
    setDownloading(true);
    try {
      const { data, headers } = await api.get(
        `/public/files/scope/${encodeURIComponent(slug)}/document`,
        { responseType: "blob" },
      );
      if (await blobLooksLikeApiError(data)) {
        throw new Error("Download failed");
      }
      const fallback = uploadedFileName({
        name: packet?.file_name || "scope-invoice.pdf",
        original_filename: packet?.file_name || "scope-invoice.pdf",
      });
      const resolvedName = downloadFilenameFromHeaders(headers, fallback);
      const mime = resolveUploadedContentType(
        { name: resolvedName, original_filename: resolvedName },
        headers["content-type"],
      );
      const blobUrl = URL.createObjectURL(new Blob([data], { type: mime || "application/pdf" }));
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = resolvedName;
      link.rel = "noopener";
      link.click();
      URL.revokeObjectURL(blobUrl);
    } catch (err) {
      const raw = await readApiErrorDetail(err);
      toast.error(formatApiDetail(raw, "Could not download PDF"));
    } finally {
      setDownloading(false);
    }
  }, [downloading, slug, packet?.file_name]);

  if (loading) {
    return <div className="min-h-screen bg-[var(--t-bg)] p-12 text-[var(--t-meta)]">Loading…</div>;
  }
  if (error || !payload) {
    return (
      <div className="mx-auto max-w-lg px-6 py-20 text-center" data-testid="scope-packet-missing">
        <p className="font-heading text-2xl !font-extralight tracking-tight">
          {error || "Scope packet not found"}
        </p>
        <p className="mt-3 text-sm text-[var(--t-meta)]">
          Ask your TAĪSTU contact for a fresh link, or open the scope from Library when signed in.
        </p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--t-bg)]" data-testid="scope-packet-page">
      <div className="sticky top-0 z-20 border-b border-[var(--t-border)]/60 bg-[var(--t-bg)]/90 backdrop-blur-sm">
        <div className="mx-auto flex max-w-3xl items-center justify-end gap-3 px-4 py-3 sm:px-6">
          <button
            type="button"
            onClick={handleDownload}
            disabled={downloading}
            className="t-btn-ghost inline-flex shrink-0 items-center gap-2 px-3 py-2 text-xs disabled:opacity-50"
            data-testid="scope-packet-download"
          >
            <Download size={14} strokeWidth={1.5} aria-hidden />
            {downloading ? "Downloading…" : "Download PDF"}
          </button>
        </div>
      </div>

      <div className="mx-auto max-w-3xl px-4 py-10 sm:px-6 md:py-14">
        <div className="label-eyebrow mb-2">TAĪSTU</div>
        <h1 className="font-heading text-3xl tracking-tight !font-extralight md:text-4xl">
          {activeLabel}
        </h1>
        <p className="mt-2 text-sm text-[var(--t-meta)]">
          {payload.client_name || client?.name || "Partner"}
          {subtitle ? ` · ${subtitle}` : ""}
        </p>

        <div className="relative mt-8" data-testid="scope-packet-carousel">
          {pageCount > 1 ? (
            <div className="mb-3 flex items-center justify-between gap-3">
              <button
                type="button"
                disabled={safePage <= 0}
                onClick={() => goToPage(safePage - 1)}
                className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[var(--t-accent)] text-[var(--t-accent)] transition-opacity disabled:opacity-30"
                aria-label="Previous page"
                data-testid="scope-packet-prev"
              >
                <ChevronLeft size={18} strokeWidth={1.4} />
              </button>
              <div className="min-w-0 text-center text-[10px] uppercase tracking-[0.14em] text-[var(--t-meta)]">
                {activeLabel}
                <span className="text-[var(--t-meta)]">
                  {" "}
                  · {safePage + 1} / {pageCount}
                </span>
              </div>
              <button
                type="button"
                disabled={safePage >= pageCount - 1}
                onClick={() => goToPage(safePage + 1)}
                className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[var(--t-accent)] text-[var(--t-accent)] transition-opacity disabled:opacity-30"
                aria-label="Next page"
                data-testid="scope-packet-next"
              >
                <ChevronRight size={18} strokeWidth={1.4} />
              </button>
            </div>
          ) : null}

          <div
            ref={emblaRef}
            className="overflow-hidden"
            style={{
              height: trackHeight != null ? trackHeight : undefined,
              transition: "height 200ms ease",
            }}
            data-testid="scope-packet-scroller"
          >
            <div className="flex touch-pan-y">
              {pages.map((entry, i) => (
                <div
                  key={entry.kind}
                  ref={(node) => {
                    slideRefs.current[i] = node;
                  }}
                  className="min-w-0 shrink-0 grow-0 basis-full"
                  data-testid={`scope-packet-slide-${entry.kind}`}
                >
                  <div className="overflow-hidden rounded-2xl border border-[var(--t-border)] bg-white p-2 sm:p-4">
                    {entry.kind === "invoice" ? (
                      <div
                        className="blu-document-preview"
                        style={{
                          width: "100%",
                          boxSizing: "border-box",
                          background: "#FCFCFC",
                          color: "#222222",
                          borderRadius: 12,
                          padding: "22px 22px 20px",
                        }}
                      >
                        <InvoiceSection payload={payload} locked={false} />
                      </div>
                    ) : (
                      <BLUPreview
                        payload={payload}
                        interactive={false}
                        scopeOnly
                        invoiceLocked={false}
                      />
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {pageCount > 1 ? (
            <div className="mt-6 flex items-center justify-center gap-1.5">
              {pages.map((entry, i) => (
                <button
                  key={entry.kind}
                  type="button"
                  onClick={() => goToPage(i)}
                  className={`h-1.5 rounded-full transition-all ${
                    i === safePage ? "w-5 bg-[var(--t-accent)]" : "w-1.5 bg-[var(--t-border)]"
                  }`}
                  aria-label={`Go to ${entry.label}`}
                  data-testid={`scope-packet-dot-${entry.kind}`}
                />
              ))}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function clientSlugFallback(client) {
  if (!client) return "";
  const stored = String(client.slug || "").trim().replace(/-/g, "");
  if (stored) return stored;
  return slugifyClientName(client.name);
}
